import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import type { OcdTheme } from '../services/settingsTypes';
import { ocdThemesService } from '../services/ocdThemesService';
import { OcdThemeEditModal } from './OcdThemeEditModal';
import { OcdThemeRow } from './OcdThemeRow';

export const OcdThemesManager = () => {
    const [themes, setThemes] = useState<OcdTheme[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingTheme, setEditingTheme] = useState<OcdTheme | null>(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    useEffect(() => {
        fetchThemes();
    }, []);

    const fetchThemes = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const fetchedThemes = await ocdThemesService.getOcdThemes();
            // Sort by order
            const sortedThemes = [...fetchedThemes].sort((a, b) => a.order - b.order);
            setThemes(sortedThemes);
        } catch (err) {
            console.error('Failed to fetch OCD themes:', err);
            setError('Failed to load OCD themes');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (theme: OcdTheme) => {
        setEditingTheme(theme);
    };

    const handleAddNew = () => {
        setIsAddingNew(true);
    };

    const handleDelete = async (theme: OcdTheme) => {
        if (!confirm(`Are you sure you want to delete "${theme.friendlyName}"?`)) {
            return;
        }

        try {
            await ocdThemesService.deleteOcdTheme(theme.key);
            setThemes(themes.filter(t => t.key !== theme.key));
        } catch (err) {
            console.error('Failed to delete theme:', err);
            setError('Failed to delete theme');
        }
    };

    const handleSave = async (theme: OcdTheme, isNew: boolean) => {
        try {
            if (isNew) {
                await ocdThemesService.addOcdTheme(theme);
            } else {
                await ocdThemesService.updateOcdTheme(theme.key, theme);
            }
            await fetchThemes();
            setEditingTheme(null);
            setIsAddingNew(false);
        } catch (err) {
            console.error('Failed to save theme:', err);
            throw err;
        }
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === index) {
            return;
        }

        const newThemes = [...themes];
        const draggedTheme = newThemes[draggedIndex];
        newThemes.splice(draggedIndex, 1);
        newThemes.splice(index, 0, draggedTheme);

        setThemes(newThemes);
        setDraggedIndex(index);
    };

    const handleDragEnd = async () => {
        if (draggedIndex === null) {
            return;
        }

        try {
            await ocdThemesService.reorderOcdThemes(themes);
        } catch (err) {
            console.error('Failed to reorder themes:', err);
            setError('Failed to save theme order');
            await fetchThemes(); // Reload to reset order
        } finally {
            setDraggedIndex(null);
        }
    };

    const handleInitializeThemes = async () => {
        if (!confirm('This will initialize the themes with default values from the app. Continue?')) {
            return;
        }

        const defaultThemes: OcdTheme[] = [
            { key: 'contamination', friendlyName: 'Contamination', description: '', isPublished: true, order: 0 },
            { key: 'health-anxiety', friendlyName: 'Health Anxiety', description: '', isPublished: true, order: 1 },
            { key: 'harm', friendlyName: 'Harm', description: '', isPublished: true, order: 2 },
            { key: 'responsibility-negligence', friendlyName: 'Responsibility / Negligence', description: '', isPublished: true, order: 3 },
            { key: 'sexual', friendlyName: 'Sexual', description: '', isPublished: true, order: 4 },
            { key: 'pedophilia', friendlyName: 'Pedophilia', description: '', isPublished: true, order: 5 },
            { key: 'relationships', friendlyName: 'Relationships', description: '', isPublished: true, order: 6 },
            { key: 'religion-moral', friendlyName: 'Religion / Moral', description: '', isPublished: true, order: 7 },
            { key: 'identity', friendlyName: 'Identity', description: '', isPublished: true, order: 8 },
            { key: 'just-right-perfectionism', friendlyName: 'Just Right / Perfectionism', description: '', isPublished: true, order: 9 },
            { key: 'existential', friendlyName: 'Existential', description: '', isPublished: true, order: 10 },
            { key: 'sensorimotor-hyperawareness', friendlyName: 'Sensorimotor / Hyperawareness', description: '', isPublished: true, order: 11 },
            { key: 'pregnancy-postpartum', friendlyName: 'Pregnancy / Postpartum', description: '', isPublished: true, order: 12 },
            { key: 'magical-thinking', friendlyName: 'Magical Thinking', description: '', isPublished: true, order: 13 },
            { key: 'false-memory-past-events', friendlyName: 'False Memory / Past Events', description: '', isPublished: true, order: 14 },
            { key: 'checking-doubt', friendlyName: 'Checking / Doubt', description: '', isPublished: true, order: 15 },
        ];

        try {
            await ocdThemesService.updateOcdThemes(defaultThemes);
            await fetchThemes();
        } catch (err) {
            console.error('Failed to initialize themes:', err);
            setError('Failed to initialize themes');
        }
    };

    if (isLoading) {
        return <div className="text-gray-600">Loading OCD themes...</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">OCD Themes</h2>
                <div className="flex flex-wrap gap-2">
                    {themes.length === 0 && (
                        <Button variant="secondary" size="sm" onClick={handleInitializeThemes}>
                            Initialize Default Themes
                        </Button>
                    )}
                    <Button variant="primary" size="sm" onClick={handleAddNew}>
                        Add Theme
                    </Button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {themes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    No OCD themes configured. Click "Initialize Default Themes" to get started.
                </div>
            ) : (
                <div className="space-y-2">
                    {themes.map((theme, index) => (
                        <OcdThemeRow
                            key={theme.key}
                            theme={theme}
                            index={index}
                            isDragging={draggedIndex === index}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {(editingTheme || isAddingNew) && (
                <OcdThemeEditModal
                    theme={editingTheme}
                    isNew={isAddingNew}
                    onSave={handleSave}
                    onClose={() => {
                        setEditingTheme(null);
                        setIsAddingNew(false);
                    }}
                />
            )}
        </div>
    );
};

